// Collection infinite scroll
// Fetches the next page via Shopify's section_id rendering API and appends
// new product cards as the user scrolls. Falls back to native pagination
// when JS is disabled — the pagination markup still renders server-side.
class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) return;
    this.init();
    this.watchForGridReplacement();
  }

  // Read state from the current DOM. Called on connect and again whenever
  // facets.js replaces #ProductGridContainer.innerHTML (which wipes our
  // sentinel and pagination links).
  init() {
    this.cleanup();
    this.grid = this.querySelector('#product-grid');
    if (!this.grid) return;

    this.nextPageUrl = this.readNextPageUrl(this);
    if (!this.nextPageUrl) {
      this.classList.remove('infinite-scroll-active');
      return;
    }

    this.loading = false;
    this.classList.add('infinite-scroll-active');
    this.setupSentinel();
  }

  // facets.js does:
  //   document.getElementById('ProductGridContainer').innerHTML = ...
  // Watch for that replacement and re-init so the next-page link is current.
  watchForGridReplacement() {
    this.gridContainerObserver = new MutationObserver(() => {
      // Debounce-ish: only re-init when we no longer have a live grid that
      // matches the one we last saw.
      const liveGrid = this.querySelector('#product-grid');
      if (liveGrid && liveGrid !== this.grid) {
        this.init();
      }
    });
    this.gridContainerObserver.observe(this, { childList: true, subtree: true });
  }

  // The pagination snippet labels the *next* page link with the
  // `pagination__item--prev` class (it's a flipped arrow icon class, not a
  // typo'd nav direction). Use the `aria-label` to be safe across themes.
  readNextPageUrl(root) {
    const next =
      root.querySelector('.pagination .pagination__item--prev') ||
      root.querySelector('.pagination a[aria-label*="Next" i], .pagination a[aria-label*="next" i]');
    return next?.href || null;
  }

  setupSentinel() {
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    // Force layout presence so any generic theme rule (empty divs, etc.)
    // cannot hide the sentinel. IntersectionObserver requires the element
    // to participate in layout to detect intersection.
    this.sentinel.style.cssText = 'display:block;height:1px;width:100%;pointer-events:none;';
    this.grid.parentNode.insertBefore(this.sentinel, this.grid.nextSibling);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadMore();
            break;
          }
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  async loadMore() {
    if (this.loading || !this.nextPageUrl) return;
    this.loading = true;

    try {
      const url = new URL(this.nextPageUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newCards = doc.querySelectorAll('#product-grid > li');

      if (newCards.length === 0) {
        this.cleanup();
        return;
      }

      const fragment = document.createDocumentFragment();
      newCards.forEach((card) => fragment.appendChild(card));
      this.grid.appendChild(fragment);

      // Find the next-next page link in the response so we can keep going.
      this.nextPageUrl = this.readNextPageUrl(doc);
      if (!this.nextPageUrl) this.cleanup();
    } catch (err) {
      console.error('Infinite scroll fetch failed:', err);
      // Stop trying — leave the native pagination links visible as fallback.
      this.cleanup();
    } finally {
      this.loading = false;
    }
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.remove();
    }
    this.sentinel = null;
  }

  disconnectedCallback() {
    this.cleanup();
    if (this.gridContainerObserver) {
      this.gridContainerObserver.disconnect();
      this.gridContainerObserver = null;
    }
  }
}

customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
